// src/components/GuestBookingForm.jsx
// Flujo de reserva para Invitados (Guest) con UI idéntica a BookingForm:
// - 3 pasos (Info -> Confirmación y Pago -> Completado)
// - Selector de modalidad (Presencial / Telemedicina)
// - PayPal Standard (redirección) para invitados (igual que BookingForm)
// - Fallback: si no se puede crear orden de pago (guest), crea reserva temporal por correo
// - Se habilita "Pagar en consultorio" también para Telemedicina, mostrando el texto "Pagarle al médico directamente"

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Card,
  CardContent,
  Stack,
  Chip,
  useTheme,
  alpha,
  Container,
  StepConnector,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  MedicalServices as MedicalIcon,
  Notes as NotesIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  LocalHospital as LocalHospitalIcon,
  LocationOn as LocationOnIcon,
  VideoCameraFront as VideoIcon,
  Storefront as PresencialIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../axiosInstance';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Pasos del proceso de reserva
const steps = ['Información del Paciente', 'Confirmación y Pago', 'Completado'];

// Stepper styles (idénticos al BookingForm)
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  ['&.MuiStepConnector-alternativeLabel']: { top: 22 },
  ['& .MuiStepConnector-line']: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
  ['&.MuiStepConnector-active .MuiStepConnector-line']: {
    backgroundImage: `linear-gradient(95deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.main} 100%)`,
  },
  ['&.MuiStepConnector-completed .MuiStepConnector-line']: {
    backgroundImage: `linear-gradient(95deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.main} 100%)`,
  },
}));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 4px 10px 0 rgba(0,0,0,.15)',
  ...(ownerState.active && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: '0 6px 15px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`,
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;
  const icons = {
    1: <PersonIcon />,
    2: <PaymentIcon />,
    3: <CheckIcon />,
  };
  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

// Selector de modalidad (idéntico al BookingForm)
const ModeSelector = ({ value, onChange }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="body1" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        Modalidad de la cita
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, val) => val && onChange(val)}
        color="primary"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <ToggleButton
          value="Presencial"
          sx={{
            px: 3,
            py: 2,
            borderRadius: '20px',
            minWidth: 220,
            height: 60,
            bgcolor: value === 'Presencial' ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            borderColor: theme.palette.primary.main,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.18),
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PresencialIcon />
            <Typography variant="h6" fontWeight="600">Presencial</Typography>
          </Stack>
        </ToggleButton>

        <ToggleButton
          value="Telemedicina"
          sx={{
            px: 3,
            py: 2,
            borderRadius: '20px',
            minWidth: 220,
            height: 60,
            bgcolor: value === 'Telemedicina' ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            borderColor: theme.palette.primary.main,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.18),
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <VideoIcon />
            <Typography variant="h6" fontWeight="600">Telemedicina</Typography>
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {value === 'Presencial'
          ? 'Te presentas en el consultorio en la fecha y hora seleccionadas.'
          : 'Recibirás un enlace de videollamada al confirmar el pago.'}
      </Typography>
    </Box>
  );
};

const GuestBookingForm = ({ doctorId, selectedSlot, onSuccess, onCancel, onProcessingChange }) => {
  const theme = useTheme();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Orden de pago (cuando el backend permite payflow a invitados)
  const [paymentOrder, setPaymentOrder] = useState(null);

  // Fallback a reserva por correo (cuando backend no permite orden de pago para guest)
  const [fallbackMode, setFallbackMode] = useState(false);
  const [reservation, setReservation] = useState(null);

  const [patientInfo, setPatientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
  });

  const [modalidad, setModalidad] = useState('Presencial');
  const [formErrors, setFormErrors] = useState({});

  // Cargar info pública del médico
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/doctors/${doctorId}/public/`);
        setDoctor(res.data);
        setError(null);
      } catch (e) {
        console.error('Error fetching doctor info:', e);
        setError('No se pudo cargar la información del médico');
      } finally {
        setLoading(false);
      }
    };
    if (doctorId) fetchDoctorInfo();
  }, [doctorId]);

  // Validación simple
  const validateForm = () => {
    const errors = {};
    if (!patientInfo.name.trim()) errors.name = 'El nombre es requerido';
    if (!patientInfo.email.trim()) errors.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientInfo.email)) errors.email = 'El email no es válido';
    if (!patientInfo.phone.trim()) errors.phone = 'El teléfono es requerido';
    else if (!/^\+?[\d\s\-()]+$/.test(patientInfo.phone)) errors.phone = 'El teléfono no es válido';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPatientInfo((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Paso 1 -> 2: crear orden de pago si el backend lo permite para guest.
  // Si retorna 401/403/405, hacemos fallback a reserva-temporal por correo.
  const handleContinueToPayment = async () => {
    if (!validateForm()) return;
    setProcessingPayment(true);
    onProcessingChange?.(true);
    setError(null);

    try {
      const payload = {
        doctorId,
        slotStart: selectedSlot?.rawStart || selectedSlot?.start?.toISOString() || selectedSlot?.start,
        slotEnd: selectedSlot?.rawEnd || selectedSlot?.end?.toISOString() || selectedSlot?.end,
        patientInfo,
        modalidad,
      };

      const response = await axiosInstance.post('/appointments/create-payment-order/', payload);
      setPaymentOrder(response.data);
      setActiveStep(1);
      setFallbackMode(false);
    } catch (err) {
      const status = err?.response?.status;
      // Fallback: no se pudo crear orden (auth/permissions) -> usa guest-booking por correo
      if (status === 401 || status === 403 || status === 405) {
        try {
          const gb = await axiosInstance.post('/appointments/guest-booking/', {
            doctorId,
            slotStart: selectedSlot?.rawStart || selectedSlot?.start?.toISOString() || selectedSlot?.start,
            slotEnd: selectedSlot?.rawEnd || selectedSlot?.end?.toISOString() || selectedSlot?.end,
            modalidad, // importante propagar
            patientInfo: {
              email: patientInfo.email,
              name: patientInfo.name,
              phone: patientInfo.phone,
              reason: patientInfo.reason,
            },
          });
          setReservation(gb.data);
          setFallbackMode(true);
          setActiveStep(1);
        } catch (e2) {
          console.error('Error en guest-booking fallback:', e2);
          setError(e2?.response?.data?.error || 'No se pudo continuar el proceso de reserva para invitado.');
        }
      } else {
        console.error('Error creando orden de pago:', err);
        setError(err?.response?.data?.error || 'Error al procesar la solicitud. Por favor, intenta nuevamente.');
      }
    } finally {
      setProcessingPayment(false);
      onProcessingChange?.(false);
    }
  };

  // Redirección a PayPal Standard (Link parametrizado)
  const handleRedirectToPayPal = async () => {
    try {
      if (!paymentOrder?.id) {
        setError('Tu orden de pago no está lista. Vuelve al paso anterior.');
        return;
      }
      setProcessingPayment(true);
      onProcessingChange?.(true);
      const resp = await axiosInstance.post('/paypal/standard-link/', { orderId: paymentOrder.id });
      const url = resp.data?.payment_url;
      if (url) {
        window.location.href = url; // Redirige a PayPal
      } else {
        setError('No se pudo generar el enlace de PayPal.');
      }
    } catch (e) {
      console.error('Error generating PayPal Standard link:', e);
      setError(e.response?.data?.error || 'Error al generar enlace de pago.');
    } finally {
      setProcessingPayment(false);
      onProcessingChange?.(false);
    }
  };

  // Confirmar "pago en consultorio" (ahora disponible para ambas modalidades)
  // - Para Presencial: "Pagar en el consultorio"
  // - Para Telemedicina: "Pagarle al médico directamente"
  const confirmOffline = async () => {
    if (!paymentOrder?.id) {
      setError('Tu orden de pago no está lista. Vuelve al paso anterior.');
      return;
    }
    try {
      setProcessingPayment(true);
      onProcessingChange?.(true);
      await axiosInstance.post('/appointments/confirm/', {
        orderId: paymentOrder.id,
        paymentProvider: 'OFFLINE',
        modalidad,
      });
      setActiveStep(2);
    } catch (err) {
      console.error('Offline confirm error:', err);
      setError(err.response?.data?.error || 'No se pudo confirmar la cita con pago directo.');
    } finally {
      setProcessingPayment(false);
      onProcessingChange?.(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return format(d, "EEEE, d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="body1" color="text.secondary">Cargando información...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      {/* Stepper */}
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ColorlibConnector />}
        sx={{ mb: 5, pt: 2, '& .MuiStepLabel-label': { mt: 1, fontWeight: 500 } }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ColorlibStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, boxShadow: theme.shadows[2] }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Paso 1: Información del Paciente */}
      {activeStep === 0 && (
        <Box>
          {/* Resumen cita */}
          <Card
            elevation={3}
            sx={{
              mb: 4,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '5px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              },
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48, boxShadow: theme.shadows[3] }}>
                    <CalendarIcon fontSize="medium" />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600" color="primary.main">
                    Detalles de la Cita
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MedicalIcon fontSize="small" color="primary" /> Médico
                      </Typography>
                      <Typography variant="h6" fontWeight="500">
                        {doctor?.nombre_completo}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalHospitalIcon fontSize="small" color="primary" /> Especialidad
                      </Typography>
                      <Typography variant="h6" fontWeight="500">
                        {doctor?.especialidad_principal}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="primary" /> Fecha y Hora
                      </Typography>
                      <Typography variant="h6" fontWeight="500">
                        {formatDateTime(selectedSlot?.start)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                      <Typography variant="body1" fontWeight="500" color="text.secondary">
                        Costo de la consulta
                      </Typography>
                      <Chip
                        label={`${doctor?.tarifa_consulta ?? 0} ${doctor?.moneda || 'USD'}`}
                        color="primary"
                        size="medium"
                        sx={{ fontWeight: 'bold', fontSize: '1rem', px: 1, py: 2.5, borderRadius: 3 }}
                      />
                    </Stack>
                  </Grid>

                  {/* Selector de modalidad */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <ModeSelector value={modalidad} onChange={setModalidad} />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Formulario paciente */}
          <Paper
            elevation={3}
            sx={{
              p: 0,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '5px',
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
              },
            }}
          >
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 48, height: 48, boxShadow: theme.shadows[3] }}>
                  <PersonIcon fontSize="medium" />
                </Avatar>
                <Typography variant="h5" fontWeight="600" color="secondary.main">
                  Información del Paciente
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    name="name"
                    value={patientInfo.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                    placeholder="Ej: Juan Pérez García"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo Electrónico"
                    name="email"
                    type="email"
                    value={patientInfo.email}
                    onChange={handleInputChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email || 'Recibirás la confirmación en este correo'}
                    required
                    placeholder="ejemplo@correo.com"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="phone"
                    value={patientInfo.phone}
                    onChange={handleInputChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone || 'Incluye código de país si es necesario'}
                    required
                    placeholder="+1 234 567 8900"
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Motivo de la Consulta"
                    name="reason"
                    value={patientInfo.reason}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    placeholder="Describe brevemente el motivo de tu consulta..."
                    helperText="Opcional: Ayuda al médico a prepararse para tu cita"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={onCancel} disabled={processingPayment} size="large" sx={{ borderRadius: 3, px: 4, py: 1.5 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleContinueToPayment}
              disabled={processingPayment}
              startIcon={processingPayment ? <CircularProgress size={20} /> : null}
              size="large"
              sx={{ borderRadius: 3, px: 4, py: 1.5, boxShadow: theme.shadows[4] }}
            >
              {processingPayment ? 'Procesando...' : 'Continuar al Pago'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Paso 2: Confirmación y Pago (o Fallback: confirmación por correo) */}
      {activeStep === 1 && (
        <Box>
          <Card
            elevation={3}
            sx={{
              mb: 4,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '5px',
                background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
              },
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 48, height: 48, boxShadow: theme.shadows[3] }}>
                    <InfoIcon fontSize="medium" />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600" color="warning.main">
                    {fallbackMode ? 'Resumen de la Solicitud' : 'Resumen de la Cita'}
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="warning" /> Paciente
                      </Typography>
                      <Typography variant="h6" fontWeight="500">{patientInfo.name}</Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="warning" /> Email
                      </Typography>
                      <Typography variant="h6" fontWeight="500">{patientInfo.email}</Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="warning" /> Teléfono
                      </Typography>
                      <Typography variant="h6" fontWeight="500">{patientInfo.phone}</Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MedicalIcon fontSize="small" color="warning" /> Médico
                      </Typography>
                      <Typography variant="h6" fontWeight="500">{doctor?.nombre_completo}</Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="warning" /> Fecha y Hora
                      </Typography>
                      <Typography variant="h6" fontWeight="500">{formatDateTime(selectedSlot?.start)}</Typography>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon fontSize="small" color="warning" /> Modalidad
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip color="accent" label={modalidad} sx={{ fontWeight: 700, letterSpacing: 0.3 }} />
                        <Typography variant="body2" color="text.secondary">
                          {modalidad === 'Presencial' ? 'Atención en consultorio' : 'Videoconsulta (enlace tras confirmar pago)'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>

                  {!!patientInfo.reason && (
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <NotesIcon fontSize="small" color="warning" /> Motivo
                        </Typography>
                        <Typography variant="body1" sx={{ pl: 3 }}>{patientInfo.reason}</Typography>
                      </Stack>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                      <Typography variant="h6" fontWeight="600">
                        {fallbackMode ? 'Precio de la consulta' : 'Total a Pagar'}
                      </Typography>
                      <Typography variant="h4" color="warning.main" fontWeight="bold" sx={{ textShadow: '0px 1px 2px rgba(0,0,0,0.1)' }}>
                        {fallbackMode
                          ? `${doctor?.tarifa_consulta ?? 0} ${doctor?.moneda || 'USD'}`
                          : `${Number(paymentOrder?.amount || doctor?.tarifa_consulta || 0).toFixed(2)} ${paymentOrder?.currency || doctor?.moneda || 'USD'}`}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Contenedor de Pago o Fallback */}
          {!fallbackMode ? (
            <Paper
              elevation={3}
              sx={{
                p: 0,
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '5px',
                  background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
                },
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.info.main, width: 48, height: 48, boxShadow: theme.shadows[3] }}>
                    <PaymentIcon fontSize="medium" />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600" color="info.main">
                    Método de Pago
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ p: 3 }}>
                <Alert
                  severity="info"
                  sx={{ mb: 3, borderRadius: 3, boxShadow: theme.shadows[1] }}
                  icon={<InfoIcon fontSize="inherit" />}
                >
                  El pago se procesará de forma segura en PayPal. Serás redirigido al portal para completar el pago.
                </Alert>

                {processingPayment ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 6,
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      Procesando...
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      display: 'flex',
                      gap: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRedirectToPayPal}
                      disabled={processingPayment}
                      size="large"
                      sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700 }}
                    >
                      Pagar con PayPal
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          ) : (
            // Fallback: Confirmación por correo (sin PayPal)
            <Paper
              elevation={3}
              sx={{
                p: 0,
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '5px',
                  background: `linear-gradient(90deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
                },
              }}
            >
              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.info.main, width: 48, height: 48, boxShadow: theme.shadows[3] }}>
                    <InfoIcon fontSize="medium" />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600" color="info.main">
                    Confirmación por correo
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Hemos enviado una solicitud a tu correo ({patientInfo.email}). Sigue las instrucciones para confirmar tu cita.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Si no ves el correo en unos minutos, revisa tu carpeta de spam.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Footer de acciones en paso 2 */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Divider sx={{ my: 2 }}>o</Divider>
            <Button
              variant="outlined"
              color="primary"
              onClick={confirmOffline}
              disabled={processingPayment}
              sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700 }}
            >
              {modalidad === 'Telemedicina' ? 'Pagarle al médico directamente' : 'Pagar en el consultorio'}
            </Button>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-start" sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => { setActiveStep(0); setError(null); }}
              disabled={processingPayment}
              size="large"
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 3, px: 4, py: 1.5 }}
            >
              Volver
            </Button>

            {/* En modo fallback, permitir continuar a Completado */}
            {fallbackMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setActiveStep(2)}
                size="large"
                sx={{ borderRadius: 3, px: 4, py: 1.5, boxShadow: theme.shadows[4] }}
              >
                Entendido
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Paso 3: Completado (ajusta mensaje según sea pago/confirmado o solicitud por correo) */}
      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.light, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 4,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -10,
                left: -10,
                right: -10,
                bottom: -10,
                borderRadius: '50%',
                border: `4px solid ${alpha(theme.palette.success.main, 0.3)}`,
                animation: 'pulse 2s infinite',
              },
              '@keyframes pulse': {
                '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                '70%': { transform: 'scale(1.05)', opacity: 0.3 },
                '100%': { transform: 'scale(0.95)', opacity: 0.7 },
              },
            }}
          >
            <CheckIcon sx={{ fontSize: 60, color: 'success.main' }} />
          </Box>

          <Typography variant="h3" gutterBottom color="success.main" fontWeight="bold" sx={{ mb: 2, textShadow: '0px 1px 2px rgba(0,0,0,0.1)' }}>
            {fallbackMode ? '¡Solicitud Enviada!' : '¡Cita Confirmada!'}
          </Typography>

          <Typography variant="h6" color="text.secondary" paragraph sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            {fallbackMode
              ? <>Revisa tu correo (<strong>{patientInfo.email}</strong>) para confirmar la cita.</>
              : <>Recibirás un correo de confirmación con todos los detalles en <strong>{patientInfo.email}</strong></>}
          </Typography>

          <Card
            elevation={3}
            sx={{
              maxWidth: 600,
              margin: '0 auto',
              mb: 4,
              borderRadius: 4,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '5px',
                background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
              },
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <CalendarIcon color="success" fontSize="large" />
                  <Typography variant="h5" fontWeight="600" color="success.main">
                    Detalles de tu Cita
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MedicalIcon fontSize="small" color="success" /> Médico
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">{doctor?.nombre_completo}</Typography>
                  </Box>

                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" color="success" /> Fecha y Hora
                    </Typography>
                    <Typography variant="h6" fontWeight="medium">{formatDateTime(selectedSlot?.start)}</Typography>
                  </Box>

                  {modalidad === 'Telemedicina' ? (
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon fontSize="small" color="success" /> Modalidad
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        Telemedicina {fallbackMode ? '(recibirás el enlace al confirmar por correo)' : '(recibirás el enlace por correo)'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon fontSize="small" color="success" /> Dirección
                      </Typography>
                      <Typography variant="h6">{doctor?.direccion_consultorio || 'Por confirmar'}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={() => onSuccess?.(fallbackMode ? reservation : paymentOrder)}
            sx={{ borderRadius: 3, px: 6, py: 1.5, fontSize: '1.1rem', boxShadow: theme.shadows[4] }}
          >
            Finalizar
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default GuestBookingForm;
